import { useRef, type CSSProperties } from "react";
import type { CatalogCard } from "../../api/client";
import { Card } from "../Card";
import { catalogToCardView } from "../../logic/catalogToCardView";
import { pixelTheme } from "../../theme/pixel";
import { E32 } from "../../theme/endesga32";
import { primaryBtn } from "../../theme/styles";
import { useModalFocus } from "../../logic/useModalFocus";

interface CardZoomModalProps {
  card: CatalogCard | null;
  onClose: () => void;
}

// Click-to-zoom for a card pulled out of the marquee. Renders the
// shared Card component at `size="hero"` against a dim backdrop.
// Escape and backdrop click both close.
export function CardZoomModal({ card, onClose }: CardZoomModalProps) {
  if (!card) return null;
  return <CardZoomModalBody card={card} onClose={onClose} />;
}

function CardZoomModalBody({
  card,
  onClose,
}: {
  card: CatalogCard;
  onClose: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onClose);

  const backdrop: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(24, 20, 37, 0.82)",
    zIndex: 100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  };

  const closeBtnProps = primaryBtn(pixelTheme);

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      style={backdrop}
      role="dialog"
      aria-modal="true"
      aria-label={`${card.name} card preview`}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div style={{ position: "relative" }}>
        <button
          className={closeBtnProps.className}
          style={{
            ...closeBtnProps.style,
            position: "absolute",
            top: -44,
            right: -4,
            boxShadow: `2px 2px 0 0 ${E32.ink}`,
          }}
          onClick={onClose}
        >
          CLOSE
        </button>
        <div style={{ width: 320 }}>
          <Card card={catalogToCardView(card)} size="hero" theme={pixelTheme} />
        </div>
      </div>
    </div>
  );
}
